<template>
    <rkm-overlay :show="loading">
        <rkm-form>
            <Tabs
                v-if="id"
                :additionals="additionals"
                :form="form"
                :id="id"
                :model="model"
                ref="Tabs"
            />

            <Wizard
                v-else
                :additionals="additionals"
                :form="form"
                :model="model"
                @finish="save"
                ref="Wizard"
            />
        </rkm-form>
    </rkm-overlay>
</template>

<script>
// Components.
import Tabs from '@/components/cadastro/cidadao/Tabs'
import Wizard from '@/components/cadastro/cidadao/Wizard'

// Enums.
import {
    CertidaoModelo,
    Pais,
    PreferenciaNome,
} from '@/enums'

// Mixins.
import {error, notification} from '@/mixins'

// Others.
import moment from 'moment'

// Utils.
import format from '@/utils/format'
import unmask from '@/utils/unmask'

// Vuex.
import {mapActions, mapGetters} from 'vuex'

export default {
    components: {
        Tabs,
        Wizard,
    },
    computed: {
        ...mapGetters(['getLoading']),
        loading() {
            return this.getLoading('cadastro-cidadao-form')
        },
    },
    data: () => ({
        additionals: {
            data_atual: null,
            permite_alteracao_endereco: false,
        },
        enums: {
            CertidaoModelo,
            Pais,
            PreferenciaNome,
        },
        form: {
            certidao_modelos: [],
            certidao_tipos: [],
            classificacoes: [],
            cnh_categorias: [],
            comorbidades: [],
            deficiencias: [],
            escolaridades: [],
            esportes: [],
            estados: [],
            estados_civis: [],
            etnias_indigena: [],
            faixas_renda: [],
            identidades_genero: [],
            locais_nascimento: [],
            logradouro_tipos: [],
            mobilidades_reduzidas: [],
            nacionalidades: [],
            orientacoes_sexuais: [],
            paises: [],
            preferencias_nome: [],
            racas_cores: [],
            sexos: [],
            situacoes_familiar_conjugais: [],
            tipos_moradia: [],
            tipos_sanguineos: [],
            zonas: [],
        },
        model: {
            // Aba dados.
            prontuario: null,
            preferencia_nome_id: PreferenciaNome.NOME,
            sexo_id: null,
            situacao_familiar_conjugal_id: null,
            classificacao_id: null,
            local_nascimento_id: null,
            pais_nascimento_id: null,
            nacionalidade_id: null,
            estado_nascimento_id: null,
            municipio_nascimento_id: null,
            raca_cor_id: null,
            etnia_indigena_id: null,
            deficiencia_id: null,
            mobilidade_reduzida_id: null,
            data_atualizacao_cadastro: null,
            cpf_numero: null,
            nome: null,
            nome_social: null,
            data_nascimento: null,
            data_obito: null,
            nome_mae: null,
            desconhece_mae: null,
            nome_pai: null,
            desconhece_pai: null,
            data_naturalizacao: null,
            municipio_nascimento_nome: null,
            deficiencia_outra: null,

            // Aba documentos.
            titulo_eleitor_estado_id: null,
            titulo_eleitor_municipio_id: null,
            rg_estado_id: null,
            certidao_tipo_id: null,
            certidao_modelo_id: CertidaoModelo.NOVO,
            certidao_estado_id: null,
            certidao_municipio_id: null,
            ctps_estado_id: null,
            cnh_estado_id: null,
            cnh_categoria_id: null,
            titulo_eleitor_numero: null,
            titulo_eleitor_municipio_nome: null,
            titulo_eleitor_zona: null,
            titulo_eleitor_secao: null,
            rg_numero: null,
            rg_data_emissao: null,
            certidao_numero: null,
            certidao_termo: null,
            certidao_cartorio_nome: null,
            certidao_livro: null,
            certidao_folha: null,
            certidao_data_emissao: null,
            certidao_municipio_nome: null,
            certidao_cartorio_codigo: null,
            ctps_numero: null,
            ctps_serie: null,
            ctps_data_emissao: null,
            ctps_nis_pis_pasep: null,
            cnh_numero: null,
            cnh_data_emissao: null,
            cnh_data_validade: null,
            rne_numero: null,
            data_entrada_brasil: null,
            
            // Aba contatos.
            telefone_residencial: null,
            telefone_celular: null,
            telefone_recado: null,
            email: null,

            // Aba endereço.
            pais_id: Pais.BRASIL,
            estado_id: null,
            municipio_id: null,
            bairro_id: null,
            logradouro_tipo_id: null,
            logradouro_id: null,
            loteamento_id: null,
            zona_id: null,
            tipo_moradia_id: null,
            logradouro_cep: null,
            municipio_nome: null,
            bairro_nome: null,
            logradouro_nome: null,
            numero: null,
            sem_numero: null,
            loteamento_nome: null,
            complemento: null,
            ponto_referencia: null,

            // Aba informações complementares.
            convenio_medico_id: null,
            convenio_odontologico_id: null,
            plano_funerario_id: null,
            estado_civil_id: null,
            escolaridade_id: null,
            tipo_sanguineo_id: null,
            orientacao_sexual_id: null,
            identidade_genero_id: null,
            ocupacao_id: null,
            faixa_renda_id: null,
            comorbidade_id: null,
            esporte_id: null,
            convenio_medico_nome: null,
            convenio_odontologico_nome: null,
            plano_funerario_nome: null,
            ocupacao_descricao: null,
            nome_conjuge: null,
            esta_empregado_atualmente: false,
            aposentado_pensionista: false,
            portador_alguma_comorbidade: false,
            utiliza_transporte_coletivo: false,
            pratica_algum_esporte: false,
            doador_orgao: false,
        },
        others: {moment},
        utils: {format, unmask},
    }),
    inject: ['repositories'],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading('cadastro-cidadao-form')

            if (this.id) {
                this.repositories.cadastro.cidadao.edit(this.id)
                    .then(({ data }) => {
                        this.setAdditionals(data.additionals)
                        this.setForm(data.form)
                        this.setModel(data.model)

                        if (this.others.moment(data.model.data_atualizacao_cadastro) < this.others.moment(data.additionals.data_atual, 'DD/MM/YYYY')) {
                            this.showWarningNotification('Atualização do cadastro expirada', 'Por favor atualize o cadastro do cidadão.')
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading('cadastro-cidadao-form'))
            } else {
                this.repositories.cadastro.cidadao.create()
                    .then(({ data }) => {
                        this.setAdditionals(data.additionals)
                        this.setForm(data.form)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading('cadastro-cidadao-form'))
            }
        },
        setAdditionals(additionals) {
            this.additionals.data_atual = additionals.data_atual
            this.additionals.permite_alteracao_endereco = additionals.permite_alteracao_endereco
        },
        setForm(form) {
            this.form.certidao_modelos = form.certidao_modelos
            this.form.certidao_tipos = form.certidao_tipos
            this.form.classificacoes = form.classificacoes
            this.form.cnh_categorias = form.cnh_categorias
            this.form.comorbidades = form.comorbidades
            this.form.deficiencias = form.deficiencias
            this.form.escolaridades = form.escolaridades
            this.form.esportes = form.esportes
            this.form.estados = form.estados
            this.form.estados_civis = form.estados_civis
            this.form.etnias_indigena = form.etnias_indigena
            this.form.faixas_renda = form.faixas_renda
            this.form.identidades_genero = form.identidades_genero
            this.form.locais_nascimento = form.locais_nascimento
            this.form.logradouro_tipos = form.logradouro_tipos
            this.form.mobilidades_reduzidas = form.mobilidades_reduzidas
            this.form.nacionalidades = form.nacionalidades
            this.form.orientacoes_sexuais = form.orientacoes_sexuais
            this.form.paises = form.paises
            this.form.preferencias_nome = form.preferencias_nome
            this.form.racas_cores = form.racas_cores
            this.form.sexos = form.sexos
            this.form.situacoes_familiar_conjugais = form.situacoes_familiar_conjugais
            this.form.tipos_moradia = form.tipos_moradia
            this.form.tipos_sanguineos = form.tipos_sanguineos
            this.form.zonas = form.zonas
        },
        setModel(model) {
            // Aba dados.
            this.model.prontuario = model.prontuario
            this.model.preferencia_nome_id = model.preferencia_nome_id || this.enums.PreferenciaNome.NOME
            this.model.sexo_id = model.sexo_id
            this.model.situacao_familiar_conjugal_id = model.situacao_familiar_conjugal_id
            this.model.classificacao_id = model.classificacao_id
            this.model.local_nascimento_id = model.local_nascimento_id
            this.model.raca_cor_id = model.raca_cor_id
            this.model.etnia_indigena_id = model.etnia_indigena_id
            this.model.deficiencia_id = model.deficiencia_id
            this.model.mobilidade_reduzida_id = model.mobilidade_reduzida_id
            this.model.data_atualizacao_cadastro = model.data_atualizacao_cadastro
            this.model.cpf_numero = this.utils.format.cpf(model.cpf_numero)
            this.model.nome = model.nome
            this.model.nome_social = model.nome_social
            this.model.data_nascimento = model.data_nascimento
            this.model.data_obito = model.data_obito
            this.model.nome_mae = model.nome_mae
            this.model.desconhece_mae = !!model.desconhece_mae
            this.model.nome_pai = model.nome_pai
            this.model.desconhece_pai = !!model.desconhece_pai
            this.model.deficiencia_outra = model.deficiencia_outra

            this.$nextTick(() => {
                this.model.pais_nascimento_id = model.pais_nascimento_id
                this.model.nacionalidade_id = model.nacionalidade_id
                this.model.estado_nascimento_id = model.estado_nascimento_id
                this.model.municipio_nascimento_id = model.municipio_nascimento_id
                this.model.municipio_nascimento_nome = model.municipio_nascimento_nome
                this.model.data_naturalizacao = model.data_naturalizacao
            })

            // Aba documentos.
            this.model.titulo_eleitor_estado_id = model.titulo_eleitor_estado_id
            this.model.certidao_tipo_id = model.certidao_tipo_id,
            this.model.certidao_modelo_id = model.certidao_modelo_id || this.enums.CertidaoModelo.NOVO

            this.$nextTick(() => {
                this.model.titulo_eleitor_municipio_id = model.titulo_eleitor_municipio_id
                this.model.rg_estado_id = model.rg_estado_id
                this.model.certidao_estado_id = model.certidao_estado_id
                this.model.certidao_municipio_id = model.certidao_municipio_id
                this.model.ctps_estado_id = model.ctps_estado_id
                this.model.cnh_estado_id = model.cnh_estado_id
                this.model.cnh_categoria_id = model.cnh_categoria_id
                this.model.titulo_eleitor_numero = model.titulo_eleitor_numero
                this.model.titulo_eleitor_municipio_nome = model.titulo_eleitor_municipio_nome
                this.model.titulo_eleitor_zona = model.titulo_eleitor_zona
                this.model.titulo_eleitor_secao = model.titulo_eleitor_secao
                this.model.rg_numero = model.rg_numero
                this.model.rg_data_emissao = model.rg_data_emissao
                this.model.certidao_numero = this.utils.format.certidao(model.certidao_numero)
                this.model.certidao_termo = model.certidao_termo
                this.model.certidao_cartorio_nome = model.certidao_cartorio_nome
                this.model.certidao_livro = model.certidao_livro
                this.model.certidao_folha = model.certidao_folha
                this.model.certidao_data_emissao = model.certidao_data_emissao
                this.model.certidao_municipio_nome = model.certidao_municipio_nome
                this.model.certidao_cartorio_codigo = model.certidao_cartorio_codigo
                this.model.ctps_numero = model.ctps_numero
                this.model.ctps_serie = model.ctps_serie
                this.model.ctps_data_emissao = model.ctps_data_emissao
                this.model.ctps_nis_pis_pasep = model.ctps_nis_pis_pasep
                this.model.cnh_numero = model.cnh_numero
                this.model.cnh_data_emissao = model.cnh_data_emissao
                this.model.cnh_data_validade = model.cnh_data_validade
                this.model.rne_numero = model.rne_numero
                this.model.data_entrada_brasil = model.data_entrada_brasil
            })
            
            // Aba contatos.
            this.model.telefone_residencial = this.utils.format.telefone(model.telefone_residencial)
            this.model.telefone_celular = this.utils.format.celular(model.telefone_celular)
            this.model.telefone_recado = model.telefone_recado && model.telefone_recado.length === 10
                ? this.utils.format.telefone(model.telefone_recado)
                : this.utils.format.celular(model.telefone_recado)
            this.model.email = model.email

            // Aba endereço.
            this.model.pais_id = model.pais_id

            this.$nextTick(() => {
                this.model.logradouro_id = model.logradouro_id

                this.$nextTick(() => {
                    this.model.estado_id = model.estado_id
                    this.model.municipio_id = model.municipio_id
                    this.model.bairro_id = model.bairro_id
                    this.model.logradouro_tipo_id = model.logradouro_tipo_id    
                    this.model.loteamento_id = model.loteamento_id
                    this.model.zona_id = model.zona_id
                    this.model.tipo_moradia_id = model.tipo_moradia_id
                    this.model.logradouro_cep = this.utils.format.cep(model.logradouro_cep)
                    this.model.municipio_nome = model.municipio_nome
                    this.model.bairro_nome = model.bairro_nome
                    this.model.logradouro_nome = model.logradouro_nome
                    this.model.numero = model.numero
                    this.model.sem_numero = !!model.sem_numero
                    this.model.loteamento_nome = model.loteamento_nome
                    this.model.complemento = model.complemento
                    this.model.ponto_referencia = model.ponto_referencia
                })
            })

            // Aba informações complementares.
            this.model.convenio_medico_id = model.convenio_medico_id
            this.model.convenio_odontologico_id = model.convenio_odontologico_id
            this.model.plano_funerario_id = model.plano_funerario_id
            this.model.estado_civil_id = model.estado_civil_id
            this.model.escolaridade_id = model.escolaridade_id
            this.model.tipo_sanguineo_id = model.tipo_sanguineo_id
            this.model.orientacao_sexual_id = model.orientacao_sexual_id
            this.model.identidade_genero_id = model.identidade_genero_id
            this.model.ocupacao_id = model.ocupacao_id
            this.model.faixa_renda_id = model.faixa_renda_id
            this.model.comorbidade_id = model.comorbidade_id
            this.model.esporte_id = model.esporte_id
            this.model.convenio_medico_nome = model.convenio_medico_nome
            this.model.convenio_odontologico_nome = model.convenio_odontologico_nome
            this.model.plano_funerario_nome = model.plano_funerario_nome
            this.model.ocupacao_descricao = model.ocupacao_descricao
            this.model.nome_conjuge = model.nome_conjuge
            this.model.esta_empregado_atualmente = !!model.esta_empregado_atualmente
            this.model.aposentado_pensionista = !!model.aposentado_pensionista
            this.model.portador_alguma_comorbidade = !!model.portador_alguma_comorbidade
            this.model.utiliza_transporte_coletivo = !!model.utiliza_transporte_coletivo
            this.model.pratica_algum_esporte = !!model.pratica_algum_esporte
            this.model.doador_orgao = !!model.doador_orgao
        },
        save() {
            const request = {
                // Aba dados.
                preferencia_nome_id: this.model.preferencia_nome_id,
                sexo_id: this.model.sexo_id,
                situacao_familiar_conjugal_id: this.model.situacao_familiar_conjugal_id,
                classificacao_id: this.model.classificacao_id,
                local_nascimento_id: this.model.local_nascimento_id,
                pais_nascimento_id: this.model.pais_nascimento_id,
                nacionalidade_id: this.model.nacionalidade_id,
                municipio_nascimento_id: this.model.municipio_nascimento_id,
                raca_cor_id: this.model.raca_cor_id,
                etnia_indigena_id: this.model.etnia_indigena_id,
                deficiencia_id: this.model.deficiencia_id,
                mobilidade_reduzida_id: this.model.mobilidade_reduzida_id,
                cpf_numero: this.utils.unmask.cpf(this.model.cpf_numero) || null,
                nome: this.model.nome,
                nome_social: this.model.nome_social,
                data_nascimento: this.others.moment(this.model.data_nascimento).format('YYYY-MM-DD'),
                data_obito: this.model.data_obito ? this.others.moment(this.model.data_obito).format('YYYY-MM-DD') : null,
                nome_mae: this.model.nome_mae,
                desconhece_mae: this.model.desconhece_mae,
                nome_pai: this.model.nome_pai,
                desconhece_pai: this.model.desconhece_pai,
                data_naturalizacao: this.model.data_naturalizacao 
                    ? this.others.moment(this.model.data_naturalizacao).format('YYYY-MM-DD') : null,
                municipio_nascimento_nome: this.model.municipio_nascimento_nome,
                deficiencia_outra: this.model.deficiencia_outra,

                // Aba documentos.
                titulo_eleitor_municipio_id: this.model.titulo_eleitor_municipio_id,
                rg_estado_id: this.model.rg_estado_id,
                certidao_tipo_id: this.model.certidao_tipo_id,
                certidao_modelo_id: this.model.certidao_tipo_id ? this.model.certidao_modelo_id : null,
                certidao_municipio_id: this.model.certidao_municipio_id,
                ctps_estado_id: this.model.ctps_estado_id,
                cnh_estado_id: this.model.cnh_estado_id,
                cnh_categoria_id: this.model.cnh_categoria_id,
                titulo_eleitor_numero: this.model.titulo_eleitor_numero,
                titulo_eleitor_zona: this.model.titulo_eleitor_zona,
                titulo_eleitor_secao: this.model.titulo_eleitor_secao,
                rg_numero: this.model.rg_numero,
                rg_data_emissao: this.model.rg_data_emissao ? this.others.moment(this.model.rg_data_emissao).format('YYYY-MM-DD') : null,
                certidao_numero: this.utils.unmask.certidao(this.model.certidao_numero) || null,
                certidao_termo: this.model.certidao_termo,
                certidao_cartorio_nome: this.model.certidao_cartorio_nome,
                certidao_livro: this.model.certidao_livro,
                certidao_folha: this.model.certidao_folha,
                certidao_data_emissao: this.model.certidao_data_emissao ? this.others.moment(this.model.certidao_data_emissao).format('YYYY-MM-DD') : null,
                certidao_cartorio_codigo: this.model.certidao_cartorio_codigo,
                ctps_numero: this.model.ctps_numero,
                ctps_serie: this.model.ctps_serie,
                ctps_data_emissao: this.model.ctps_data_emissao ? this.others.moment(this.model.ctps_data_emissao).format('YYYY-MM-DD') : null,
                ctps_nis_pis_pasep: this.model.ctps_nis_pis_pasep,
                cnh_numero: this.model.cnh_numero,
                cnh_data_emissao: this.model.cnh_data_emissao ? this.others.moment(this.model.cnh_data_emissao).format('YYYY-MM-DD') : null,
                cnh_data_validade: this.model.cnh_data_validade ? this.others.moment(this.model.cnh_data_validade).format('YYYY-MM-DD') : null,
                rne_numero: this.model.rne_numero,
                data_entrada_brasil: this.model.data_entrada_brasil ? this.others.moment(this.model.data_entrada_brasil).format('YYYY-MM-DD') : null,
                
                // Aba contatos.
                telefone_residencial: this.utils.unmask.telefone(this.model.telefone_residencial) || null,
                telefone_celular: this.utils.unmask.telefone(this.model.telefone_celular) || null,
                telefone_recado: this.utils.unmask.telefone(this.model.telefone_recado) || null,
                email: this.model.email,

                // Aba endereço.
                pais_id: this.model.pais_id,
                logradouro_id: this.model.logradouro_id,
                loteamento_id: this.model.loteamento_id,
                zona_id: this.model.zona_id,
                tipo_moradia_id: this.model.tipo_moradia_id,
                numero: this.model.numero,
                sem_numero: this.model.sem_numero,
                complemento: this.model.complemento,
                ponto_referencia: this.model.ponto_referencia,

                // Aba informações complementares.
                convenio_medico_id: this.model.convenio_medico_id,
                convenio_odontologico_id: this.model.convenio_odontologico_id,
                plano_funerario_id: this.model.plano_funerario_id,
                estado_civil_id: this.model.estado_civil_id,
                escolaridade_id: this.model.escolaridade_id,
                tipo_sanguineo_id: this.model.tipo_sanguineo_id,
                orientacao_sexual_id: this.model.orientacao_sexual_id,
                identidade_genero_id: this.model.identidade_genero_id,
                ocupacao_id: this.model.ocupacao_id,
                faixa_renda_id: this.model.faixa_renda_id,
                comorbidade_id: this.model.comorbidade_id,
                esporte_id: this.model.esporte_id,
                nome_conjuge: this.model.nome_conjuge,
                esta_empregado_atualmente: this.model.esta_empregado_atualmente,
                aposentado_pensionista: this.model.aposentado_pensionista,
                portador_alguma_comorbidade: this.model.portador_alguma_comorbidade,
                utiliza_transporte_coletivo: this.model.utiliza_transporte_coletivo,
                pratica_algum_esporte: this.model.pratica_algum_esporte,
                doador_orgao: this.model.doador_orgao,
            }

            if (this.id) {
                this.$refs.Tabs.validate()
                    .then(validated => {
                        if (validated === true) {
                            this.setLoading('cadastro-cidadao-form')

                            this.repositories.cadastro.cidadao.update(request, this.id)
                                .then(({ data }) => this.$emit('save', data))
                                .catch(this.handleError)
                                .finally(() => this.unsetLoading('cadastro-cidadao-form'))
                        }
                    })
            } else {
                this.repositories.cadastro.cidadao.store(request)
                    .then(({ data }) => this.$emit('save', data))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading('cadastro-cidadao-form'))
            }
        },
    },
    mixins: [error, notification],
    mounted() {
        this.load()
    },
    props: {
        id: {type: Number},
    },
}
</script> 